/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import axios, {AxiosInstance} from 'axios';
import {isUndefined} from 'lodash';
import isString from 'lodash.isstring';
import uniq from 'lodash.uniq';
import {Moment} from 'moment';
import {speedThreshold} from '../entities/Asset';
import {
  CVSAssetType,
  Device,
  FindNearbyType,
  GenericObject,
  SCDeviceType
} from '../types/entities';
import {
  ArcGISConfig,
  ArcGISServer,
  ArcGISService,
  AxiosConfig,
  GeofenceFilter,
  GeoFenceOperation,
  QueryParams,
  ServiceError
} from '../types/services';
import {convertStringsToNumbers} from '../utils/ArrayUtil';
import {
  toISOString
} from '../utils/DateUtil';
import {
  safeJSONParse
} from '../utils/JSONUtils';
import {
  toProperties
} from '../utils/ObjectUtil';
import {
  contains,
  splitBySpecialCharacters,
  stringWithEscapeCharacters,
  toStringMapper
} from '../utils/StringUtil';
import {createAsyncClient} from './AsyncClientManager';
import {setClientAuthorizationHeader} from './ConnectUtils';
import {errorResolver} from './ServiceErrors';

export const moving    = `gpsSpeed > ${speedThreshold}`;
export const notMoving = `gpsSpeed <= ${speedThreshold}`;

// Defines the asset state mapping
export const assetStateMapping = {
  active : '(assetType = \'Machine\' AND ignition = \'true\')',
  towed  : `(((assetType <> \'Unpowered\') AND ignition = \'false\' AND ${moving}) OR (assetType = \'Unpowered\' AND ${moving}))`,
  parked : `(((assetType <> \'Unpowered\') AND ignition = \'false\' AND ${notMoving}) OR (assetType = \'Unpowered\' AND ${notMoving}))`,
  moving : `(assetType NOT IN (\'Unpowered\', \'Machine\') AND ignition = \'true\' AND ${moving})`,
  idle   : `(assetType NOT IN (\'Unpowered\', \'Machine\') AND ignition = \'true\' AND ${notMoving})`,
  nogps  : '(latitude = 0 AND longitude = 0)'
};

axios.defaults.withCredentials = true;

/**
 * Defines a refernece to the configured Axios HTTP client instance.
 */
export let client: AxiosInstance;

/**
 * Defines a reference to the CTC Configuration which is used by all requests.
 */
export let config: AxiosConfig;

/**
 * Defines CVSQueryArgs which is used by CVS Service requests.
 */
export type CVSQueryArgs = {
  geometryType?: string;
  groups?: number[];
  assets?: CVSAssetType[];
  devices?: number[] | string[];
  assetId?: number,
  searchTerm?: string;
  searchByIds?: number[];
  geoFences?: string[];
  assetTypes?: string[];
  assetCategories?: string[];
  routes?: string[];
  assetStatus?: string[];
  time?: string;
  where?: {
    searchTerm?: string,
    geoFences?: any[],
    assetTypes?: any[],
    assetCategories?: any[],
    routes?: any[],
    assetStatus?: any[],
    filter?: string
    time?: string;
    deviceLike?: string;
  };
  geometry?: number[][];
  mapUnitsPerPixel?: number;
  clusterDistanceInPixels?: number;
  sort?: string;
  outFields?: string;
  filter?: any[];
  startRow?: number;
  pageSize?: number;
  limitResults?: number;
  includeHFMCount?: boolean;
  count?: number;
  searchtype?: string;
  ops?: string;
}

/**
 * Defines HVSQueryArgs which is used by HVS Service requests.
 */
export type HVSQueryArgs = {
  groups?: number[];
  assets?: CVSAssetType[];
  start?: Date | Moment;
  end?: Date | Moment;
  geoFences?: string[];
  types?: string[];
  routes?: string[];
  categories?: string[];
  geometry?: any;
  status?: string[];
  devices?: Device[];
  hasEndDate?: boolean;
}

export type GeofenceQueryArgs = {
  value?: string;
  groupIds?: number[];
  query?: string;
  limitResults?: number;
}

/**
 * Defines FindNearbyQueryArgs which is used by the findNearby CVS request.
 */
export type FindNearbyQueryArgs = {
  latitude: number;
  longitude: number;
  parameters: CVSQueryArgs;
  miles: number;
}
/**
 * Defines FindNearbyRequestArgs which is used by the findNearby action
 */
export type FindNearbyRequestArgs = {
  latitude: number,
  longitude: number,
  miles: number,
  label: string,
  findType?: FindNearbyType
}

/**
 * Defines SCDeviceArgs used to retrieve SC Devices
 */

export type SCDeviceArgs = {
  accountid?: number[],
  assetid?: number[],
  deviceid?: number[],
  where?: string,
  outFields?: string;
}

//Defines SearchQueryArgs used to form query
export type SearchQueryArgs = {
  accountId?: number,
  ancestorAccountIds?: number[],
  onlyEnterExit?: boolean,
  groupValue?: number
};

//Defines payload for V2 API
interface V2SearchPayloadResult {
  accountid?: number[];
  assetid?: number[];
  deviceid?: number[];
  assetCategory?: string[];
  assetType?: string[];
  geometry?: GenericObject;
  geometrytype?: string;
  assetStatus?: any[];
  geoFences?: any[];
  routeId?: any[];
  landmark?: number[];
  searchterm?: string;
  searchtype?: string;
  ops?: string;
};

/**
 * Defines normalizeQueryParams which is used to include limit results as needed
 */
export const normalizeQueryParams = (params: any, limitResults: number): any => {
  if (limitResults) {
    params.resultOffset      = 0;
    params.resultRecordCount = limitResults;
  }
  return params;
};

/**
 * @private
 */
const normalized = (list: string[]) => {
  list = list.map(item => item.replace('\'', '\'\''));
  return `'${list.join('\',\'')}'`;
};

/**
 * @private
 */
const normalizeQuery = (query) => {
  return query.length ? ' AND ' : '';
};

/**
 * Defines normalizeGeoFence which is used to normalize geofences
 */
const normalizeGeoFence = (list: string[]) => {
  list = list.map(item => item.replace(item, `landmark LIKE '%${item}%' OR`));
  return `${list.join(' ')}`;
};

/**
 * Defines normalizeAssetStatus which is used to normalize asset status
 */
export const normalizeAssetStatus = (list: string[]) => {
  const count = list?.length;
  list = list.map((item, index) => item.replace(item,
    `${assetStateMapping[item?.toLocaleLowerCase()]}${(count > 1 && index !== count - 1) ? ' OR' : ''}`));
  return `${list.join(' ')}`;
};

/**
 * Provides a utility for generating a CVS Query for use within a CVS Request.
 *
 * ```typescript
 * case #1 when there are groups and assets selected
 *         In this case, query string will start as '(accountId IN (<<groupIds>>) or assetId IN (<<assetIds>>)) ...'
 * case #2 groups only
 *         In this case, query string will start as '(accountId IN (<<groupIds>>)) ...'
 * case #3 assets only
 *         In this case, query string will start as ('assetId IN (<<assetIds>>)) ... '
 * case #4 assetId only
 *         This is a one special case where the selection is from the sideSheet of the tracking map onSelect assetName
 *         In this case, query string will start as 'assetId = <<assetId> ...'
 * case #5 device only
 *         In this case, query string will be just '1=0'. CVS will return no result from this request.
 * case #6 no selection
 *         When no selection is made in the tracking filters, accountIds will be
 *         all account Ids under the selected global group (per getCheckedAccountIds method in ion/store/helpers).
 *         Therefore, there will be all accountIds passed to this function and it will return as case #2.
 * ```
 */
export const createCVSQuery = ({
  assetId,
  searchTerm,
  searchByIds,
  groups,
  assets,
  geoFences,
  assetTypes,
  assetCategories,
  routes,
  assetStatus,
  time,
  filter
}: CVSQueryArgs): string => {
  let query = groups?.length ? `accountId IN (${uniq(groups).toString()})` : '';
  const includeGroups = !!groups?.length;
  const includeAssets = !!assets?.length;
  if (includeGroups && includeAssets) {
    query = `(accountId IN (${uniq(groups).toString()}) or assetId IN (${toProperties(assets).toString()}))`;
  }
  if (includeAssets && !includeGroups) {
    query += `${getPrefixQuery(query)}assetId IN (${toProperties(assets).toString()})`;
  }
  if (assetId) {
    query += query?.length ? ' AND ' : '';
    query += `assetId = ${assetId}`;
  }
  if (searchTerm) {
    const normalizedSearchTerm = stringWithEscapeCharacters(searchTerm.replace('\'', '\'\''));
    query += ` AND ((assetName LIKE N'%${normalizedSearchTerm}%')`;
    query += ` OR (assetAlias LIKE N'%${normalizedSearchTerm}%')`;
    query += ` OR (assets LIKE N'%${normalizedSearchTerm}%'))`;
    if (searchByIds?.length) {
      query += ` OR assetId IN (${searchByIds.toString()})`;
    }
  }
  if (geoFences?.length) {
    const condition = normalizeGeoFence(geoFences.map(String));
    query += ` AND (${condition.substring(0, condition.length - 3)})`;
  }
  if (assetTypes?.length) {
    query += ` AND assetType IN (${normalized(assetTypes)})`;
  }
  if (assetCategories?.length) {
    query += ` AND assetCategory IN (${normalized(assetCategories)})`;
  }
  if (routes?.length) {
    query += ` AND routeId IN (${routes.toString()})`;
  }
  if (assetStatus?.length) {
    query += ` AND (${normalizeAssetStatus(assetStatus)})`;
  }
  if (time) {
    query += ` AND (dbWriteTime > '${time}')`;
  }
  if (filter && isString(filter)) {
    query += ` AND (${filter})`;
  }
  if (!includeAssets && !includeGroups && !assetId) {
    /* This case addresses the query with deviceOnly. For initialization
    where no item selected from the tree filter, getCheckedAccountIds method
    in helper will return all accountIds.
    */
    return '1=0';
  }
  return query;
};

/**
 * Provides a utility for creating an ArcGIS CVS Cluster API query based on the provided
 * parameters.
 *
 * ```typescript
 *
 *    import {createCVSLargeFleetQuery} from '@ion/core/services/ArcGIS';
 *
 *    const query = createCVSLargeFleetQuery(params);
 *
 * ```
 */
export const createCVSLargeFleetQuery = ({
  where: {
    searchTerm,
    geoFences,
    assetTypes,
    assetCategories,
    routes,
    assetStatus,
    filter
  }
}: CVSQueryArgs): string => {
  let query = '';
  if (searchTerm) {
    query += `((assetName LIKE '%${searchTerm}%') OR (assetAlias LIKE '%${searchTerm}%')
    OR (assets LIKE N'%${searchTerm}%'))`;
  }
  if (geoFences?.length) {
    const condition = normalizeGeoFence(geoFences.map(String));
    query +=  `${normalizeQuery(query)}${condition.substring(0, condition.length - 3)}`;
  }
  if (assetTypes?.length) {
    query += `${normalizeQuery(query)}assetType IN (${normalized(assetTypes)})`;
  }
  if (assetCategories?.length) {
    query += `${normalizeQuery(query)}assetCategory IN (${normalized(assetCategories)})`;
  }
  if (routes?.length) {
    query += `${normalizeQuery(query)}routeId IN (${routes.toString()})`;
  }
  if (assetStatus?.length) {
    query += `${normalizeQuery(query)}(${normalizeAssetStatus(assetStatus)})`;
  }
  if (filter) {
    query += `${normalizeQuery(query)}${filter}`;
  }
  return query;
};

/**
 * Provides a utility for creating an ArcGIS SC Device query based on the provided
 * parameters.
 *
 * ```typescript
 *
 *    import {createSCDeviceQuery} from '@ion/core/services/ArcGIS';
 *
 *    const query = createSCDeviceQuery(params);
 *
 * ```
 */
export const createSCDeviceQuery = ({
  where: {
    time,
    deviceLike
  } = {}
}: CVSQueryArgs): string => {
  return time ? `db_write_time > '${time}'` : `${deviceLike ?? ''}`;
};

export const createShipmentHistoryQuery = ({
  start,
  end
}: HVSQueryArgs) => {
  let query = '';
  if (start && !end) {
    query = `${getPrefixQuery(query)}(message_time > '${start.toISOString()}')`;
  }
  if (start && end) {
    query = `${getPrefixQuery(query)}(message_time>='${start.toISOString()}' AND message_time<='${end.toISOString()}')`;
  }
  return query;
};

/**
 * Provides a utility for creating an ArcGIS HVS query based on the provided
 * parameters.
 *
 * By default, a list of assets is required, as well as a start and end date.
 * In addition, a list of asset types, routes, and categories can also be
 * provided.
 *
 * ```typescript
 *
 *    import {createAssetHistoryQuery} from '@ion/core/services/ArcGIS';
 *
 *    const assets = [{id: 132443}];
 *    const start  = new Date('2020-08-05T07:00');
 *    const end    = new Date('2020-08-06T08:30');
 *    const query = createAssetHistoryQuery(asset, startDate, endDate);
 *    ` assetId IN (132443)
 *      AND (eventTime>='2020-08-05T07:00:00.000Z'
 *      AND eventTime<='2020-08-06T07:00:00.000Z)'
 *     `
 *
 * ```
 */
export const createAssetHistoryQuery = ({
  groups,
  assets,
  start,
  end,
  geoFences,
  types,
  routes,
  categories,
  status
}: HVSQueryArgs): string => {
  let query = '';
  const includeGroups = !!groups?.length;
  const includeAssets = !!assets?.length;
  if (includeGroups && includeAssets) {
    query = `(accountId IN (${uniq(groups).toString()}) or assetId IN (${toProperties(assets).toString()}))`;
  }
  if (!includeAssets && includeGroups) {
    query = `accountId IN (${uniq(groups).toString()})`;
  }
  if (includeAssets && !includeGroups) {
    query = `${getPrefixQuery(query)}assetId IN (${toProperties(assets).toString()})`;
  }
  if (start && !end) {
    query = `${getPrefixQuery(query)}(eventTime > '${start.toISOString()}')`;
  }
  if (start && end) {
    query = `${getPrefixQuery(query)}(eventTime>='${start.toISOString()}' AND eventTime<='${end.toISOString()}')`;
  }
  if (geoFences?.length) {
    const condition = normalizeGeoFence(geoFences.map(String));
    query = `${getPrefixQuery(query)}(${condition.substring(0, condition.length - 3)})`;
  }
  if (types?.length) {
    query = `${getPrefixQuery(query)}assetType IN (${toStringMapper(types)})`;
  }
  if (categories?.length) {
    query = `${getPrefixQuery(query)}assetCategory IN (${toStringMapper(categories)})`;
  }
  if (routes?.length) {
    query = `${getPrefixQuery(query)}routeId IN (${routes.toString()})`;
  }
  if (status?.length) {
    query = `${getPrefixQuery(query)}(${normalizeAssetStatus(status).toString()})`;
  }
  return query;
};

/**
 * Utility to create event time filter object for Tracking history
 * which needs to be sent in payload as array of object instaed of query injection
 *
 *  ```typescript
 *
 *  import {createDateTimeFilter} from '@ion/core/services/ArcGIS';
 *  const start  = new Date('2023-10-29');
 *  const end    = new Date('2023-11-05');
 *  const filter = createDateTimeFilter(start, end);
 *  `[{"field":"eventTime",
 *     "ops":"in range","type":"datetime",
 *     "value":"2023-10-29T18:30:00.000Z",
 *     "value1":"2023-11-05T18:29:59.999Z"}]
 *  `
 */
export const createDateTimeFilter = ({
  start,
  end,
  hasEndDate = false
}: HVSQueryArgs): Array<any> => {
  const value1ToConvert = hasEndDate ? end : start || end;
  if (start) {
    return [
      {
        field  : 'eventTime',
        ops    : 'in range',
        type   : 'datetime',
        value  : toISOString(start, hasEndDate ? 'current' : 'startOf'),
        value1 : toISOString(value1ToConvert, hasEndDate ? 'current' : 'endOf')
      }];
  } else {
    return [];
  }
};

/**
 * The utility for creating query prefix for the given filter expression
 * to the ArcGIS CVS query for retrieving all asset details.
 *
 * ```typescript
 *
 *  import {getPrefixQuery} from '@ion/core/src/services/ArcGISUtils';
 *
 *  let query = '';
 *
 *  query = `${getPrefixQuery(query)}assetId IN (2928, 123,543)`;
 *
 * ```
 */
export const getPrefixQuery = (query: string, condition: 'AND' | 'OR' = 'AND'): string => {
  if (query?.length) {
    query = `${query} ${condition} `;
  }
  return query;
};

/**
 * Provides a utility for creating an ArcGIS CVS query for retrieving all assets
 * within a given radius of a specific asset's location.
 */
export const createFindNearbyQueryParams = (
  {latitude, longitude, accountIds, miles = 50}: FindNearbyQueryArgs
): any => {
  return {
    where    : `accountId In (${accountIds})`,
    geometry : JSON.stringify({
      spatialReference : {
        wkid       : 4326,
        latestWkid : 4326
      },
      x : longitude,
      y : latitude
    }),
    distance       : miles,
    units          : 'esriSRUnit_StatuteMile',
    orderByFields  : 'dbWriteTime ASC',
    spatialRel     : 'esriSpatialRelIntersects',
    geometryType   : 'esriGeometryPoint',
    returnGeometry : true,
    outFields      : '*',
    inSR           : 4326,
    outSR          : 102100,
    f              : 'json'
  };
};

/**
 * Given a list of CVSAsset attribute names, and a list of group ids and or asset ids,
 * creates the query parameters for use with a request to retrieve those attributes
 * from all items which meet the given criteria.
 */
export const createAttributeQueryParams = (
  attributes: string[],
  groupIds?: number[],
  assetIds?: number[]) => {
  let definition = '';
  if (groupIds?.length) {
    definition = `accountId IN (${groupIds.toString()})`;
  }
  if (assetIds?.length) {
    if (definition.length) {
      definition = `(${definition} or assetId IN (${assetIds.toString()}))`;
    } else {
      definition = `assetId IN (${assetIds.toString()})`;
    }
  }
  return {
    where                : definition,
    outFields            : attributes.toString(),
    returnGeometry       : false,
    returnDistinctValues : true,
    f                    : 'json'
  };
};

/**
 * Replaces a special charactor in string with escape charactor & trim white space charectors.
 *
 * ```typescript
 *
 * import {resolveDriverSearchTerms} from './ArcGISUtils'
 * example 1:
 * const response = resolveDriverSearchTerms('C'hetan', 'OR', true); output: operatorNames like '%C''hetan%'
 * example 2:
 * const response = resolveDriverSearchTerms('C[hetan', 'OR', true); output: operatorNames like '%C[[]hetan%'
 *
 * ```
 */
export const resolveDriverSearchTerms = (
  searchTerm: string,
  operator = 'OR',
  includeFullSearchTerm = true
): string => {
  const terms       = splitBySpecialCharacters(searchTerm).filter(i => i);
  let query         = includeFullSearchTerm ? `operatorNames like '%${stringWithEscapeCharacters(searchTerm)}%'` : '';
  const validLength = includeFullSearchTerm ? 1 : 0;
  if (terms.length > validLength) {
    terms.forEach((term: string) => {
      const search   = term.replace(/(^,)|(,$)/g, '').trim();
      const resolved = stringWithEscapeCharacters(search);
      if (query.length) {
        query += ` ${operator} `;
      }
      query += `operatorNames like '%${resolved}%'`;
    });
  }
  return query || searchTerm;
};

/**
 * Given a list of CVSAssetTypes and an attribute name, returns just the values
 * of that attribute for each CVSAssetType in the list.
 */
export const filterAttributes = (items: CVSAssetType[], attributes: string[]): any[] => {
  const multi = attributes?.length > 1;
  return items.map((item: CVSAssetType) => {
    if (multi) {
      const entity = {};
      attributes.forEach(attr => {
        const val = item[attr];
        if (val) {
          entity[attr] = val;
        }
      });
      return entity;
    }
    return item[attributes[0]];
  });
};

/**
 * Provides a utility for generating a GeoFence Service Endpoint for GeoFence CRUD based
 * operations.
 */
export const createGeoFenceEndpoint = (operation: GeoFenceOperation, endpoint?: string) => {
  return `${createEndpoint(
    ArcGISService.Landmarks,
    ArcGISServer.FeatureServer,
    endpoint
  )}/${operation}Features`.replace('/query', '');
};

/**
 * Provides a utility which returns the ArcGIS service endpoint based on the provided
 * service and server values.
 *
 * ```typescript
 *
 *   // config.mapServerURL:
 *   // https://devgis1071.connect.calamp.com/qa-arcgis-proxy/proxy.ashx?https://devgis1071.connect.calamp.com/server/rest/services/QA/{service}/{server}/0/query
 *
 *   const url = createEndpoint(ArcGISService.Landmarks, ArcGISServer.MapServer);
 *   // https://devgis1071.connect.calamp.com/qa-arcgis-proxy/proxy.ashx?https://devgis1071.connect.calamp.com/server/rest/services/QA/Landmarks/MapServer/0/query
 *
 * ```
 */
export const createEndpoint = (
  service: ArcGISService,
  server?: ArcGISServer,
  mapServiceURL?: string): string => {
  const url = mapServiceURL || config.mapServiceURL;
  return url
    .replace('{service}', service)
    .replace('{server}', server || ArcGISServer.MapServer);
};

/**
 * Normalizes a form data request by stripping all whitespace characters
 * from the provided serialized form data string.
 */
export const normalize = serialized => serialized.replace(/\s/g, '');

/**
 * Function is used to add an additional apostrophe(') at every occurence of an already existing
 * apostrophe(') in the global filter text since it is considered special character in databse.
 */
export const normalizeGlobalFilterText = (filterTexts: string[]) => {
  return filterTexts.map(filter => filter.replaceAll('\'', '\'\''));
};

/**
 * Parses a generic response and returns the root level property from within
 * the response data.
 *
 * ```typescript
 *
 *   const response = await axios({...});
 *   return parse(response); // response.data.features
 *   ...
 *   return parseCollection(response, ['attributes', 'geometry']);
 *
 * ```
 *
 */
export const parse = (response, key = 'features') => {
  return response?.data?.[key];
};

/**
 * Parses a collection of items which contain an "attributes" and "geometry"
 * property, returning the properties within the attributes object as properties
 * of the top-level object.
 *
 * ```typescript
 *
 *   const response = await axios({...});
 *   ...
 *   return parseCollection(response, ['attributes', 'geometry']);
 *
 * ```
 *
 */
export const parseCollection = (response: any, keys: string[], normalizeId = true) => {
  const collection = parse(response);
  return collection?.map(item => {
    const entry = item[keys[0]];
    if (normalizeId) {
      entry.id = (entry.id || entry.assetId);
    }
    if (keys.length > 1) {
      entry.geometry = item[keys[1]];
    }
    return entry;
  });
};

/**
 * @function setArcGISConfig
 *
 * The setArcGISConfig function allows for initializing the configuration
 * of the ArcGIS Client.
 *
 * ```typescript
 *
 *    import {loadConfig} from '@ion/core/src/environment/environment';
 *    import {setArcGISConfig} from '@ion/core/src/services/ArcGISUtils';
 *
 *    loadConfig(
 *      config => {
 *        setArcGISConfig(config.arcGISServiceURL, config.cvsServiceURL);
 *        ...
 *      },
 *      ...
 *    );
 *
 * ```
 */
export const setArcGISConfig = ({
  mapServiceURL,
  cvsServiceURL,
  hvsServiceURL,
  arcGISPortalURL,
  arcGISProxyURL,
  arcGISServiceURLPrefix,
  arcGISServiceAPIPrefix
}: ArcGISConfig) => {
  config = {
    withCredentials        : true,
    baseURL                : mapServiceURL,
    mapServiceURL          : mapServiceURL,
    cvsServiceURL          : cvsServiceURL,
    hvsServiceURL          : hvsServiceURL,
    arcGISPortalURL        : arcGISPortalURL,
    arcGISProxyURL         : arcGISProxyURL,
    arcGISServiceURLPrefix : arcGISServiceURLPrefix,
    arcGISServiceAPIPrefix : arcGISServiceAPIPrefix,
    timeout                : 60000,
    headers                : {
      Accept         : 'application/json',
      'Content-Type' : 'application/x-www-form-urlencoded'
    }
  };
  client = createAsyncClient(config);
  return client;
};

/**
 * The util function to set Authorization Header in axios client
 *
 * @param token The token to set in header.
 */
export const setArcGISAuthorizationHeader = (token: string) => {
  setClientAuthorizationHeader(client, token);
};

/**
 * @setArcGISErrorHandler
 *
 * Allows for providing a global error handler for all ArcGIS requests.
 */
export const setArcGISErrorHandler = (onError: (ServiceError) => void) => {
  client?.interceptors?.response?.use(
    response => {
      const {data} = response;
      if (data?.error) {
        const error = {...data.error, response: {status: data.error.code}};
        onError(errorResolver(error));
        return Promise.reject(error);
      }
      return response;
    },
    error => onError(errorResolver(error))
  );
};

/**
 * @createGeoFenceSearchTerms
 *
 * Provides a utility which generates the query expression for a geoefence search / filter.
 */
export const createGeoFenceSearchTerms = (filters: GeofenceFilter): string => {
  let search = '';
  for (const [attribute, searchTerm] of Object.entries(filters)) {
    search += `${attribute} LIKE '%${searchTerm}%' `;
  }
  return search;
};

/**
 * The utility provides the option to create the search clause for ArcGIS Query Search. This
 * creates the condition clause based on the onlyEnterExit param. If onlyEnterExit is true then
 * returned result only contains only Enter and Exit type, else result contains all types of geofence.
 *
 * ```typescript
 *
 *  import {createSearchQuery} from '@ion/core/src/services/ArcGISUtils';
 *
 *  createSearchQuery(5099, false); // (landmarkAccountId IN (5099))
 *  createSearchQuery(5099, true);
 *  // (landmarkAccountId IN (5099) AND landmarkStatus <> \'Deleted\'
 *  // AND GeofenceType <> \'None (Landmark Only)\')'
 *
 * ```
 *
 * @param   account The account id that needs to be passed.
 * @param   ancestorAccountIds The ancestor account id that needs to be passed.
 * @param   onlyEnterExit The flag that indicates the result type. If it is true then returned
 * result   only contains only Enter and Exit type, else result contains all types of geofence.
 * @returns search query that needs to pass as a where clause in API.
 */
export const createSearchQuery = ({
  accountId,
  ancestorAccountIds,
  onlyEnterExit = false,
  groupValue = null
}: SearchQueryArgs) => {
  const suffixQuery = onlyEnterExit ? ' AND landmarkStatus <> \'Deleted\' AND GeofenceType <> \'None (Landmark Only)\'' : '';
  const selectedAccount = groupValue && groupValue !== accountId ? `${','+groupValue}` : '';
  if (!isUndefined(ancestorAccountIds) && ancestorAccountIds.length) {
    return `((landmarkAccountId IN (${ancestorAccountIds}) AND landmarkRestricted='False') or landmarkAccountId in (${accountId}${selectedAccount}))${suffixQuery}`;
  } else {
    return `(landmarkAccountId IN (${accountId}${selectedAccount})${suffixQuery})`;
  }
};

/**
 * The utility to check if object is of type Sc Devices or Assets
 *
 * ```typescript
 *
 *  import {isScDevice} from '@ion/core/src/services/ArcGISUtils';
 *
 *  isScDevice({}); // false
 *  isScDevice({isScDevice: true}); //true
 *
 * ```
 *
 * @param   object The instance of CVS or SC Device
 * @returns true or false.
 */
export const isScDevice = (object: any): object is SCDeviceType => object.isScDevice;

/**
 * The utility to check if object is of type Sc Devices or Assets
 *
 * ```typescript
 *
 *  import {isCVSAsset} from '@ion/core/src/services/ArcGISUtils';
 *
 *  isCVSAsset({}); // true
 *  isCVSAsset({isScDevice: true}); //false
 *
 * ```
 *
 * @param   object The instance of CVS or SC Device
 * @returns true or false.
 */
export const isCVSAsset = (object: any): object is CVSAssetType => !object?.isScDevice;

/**
 * createGeofenceQuery is a helper function which determines Admin Geofence query string
 * based on the passed values
 */
export const createGeofenceQuery = (
  accountIds: string,
  filterOn: GeofenceFilter | string,
  ancestorAccountIds: number[]
) => {
  if (!isUndefined(ancestorAccountIds) && ancestorAccountIds.length) {
    return `((landmarkAccountId IN (${ancestorAccountIds})
      AND landmarkRestricted='False') or landmarkAccountId in (${accountIds}))${filterOn ? ` AND (${filterOn})` : ''}`;
  } else {
    return `landmarkAccountId IN (${accountIds})${filterOn ? ` AND (${filterOn})` : ''}`;
  }
};

/**
 * The getMatchedAssets utility function returns a list of all the assets
 * that match the given search query string against the specified terms and conditions.
 *
 * ```typescript
 *
 *  import {getMatchedAssets} from '@ion/core/src/services';
 *
 *  const matchedAssets = getMatchedAssets(assets);
 *
 * ```
 *
 * @param assets     The assets to be performed into search matches.
 * @param searchTerm The search term to be used in the search
 * @returns The matched assets to be loaded into search results
 */
export const getMatchedAssets = (assets: CVSAssetType[], searchTerm: string) => {
  const terms = splitBySpecialCharacters(searchTerm, true).map(term => term.toLowerCase());
  return assets?.filter((asset: any) => (
    terms.some(term => {
      const {assetName, assetAlias, assets, externalId, isLicenseMatched} = asset;
      if (
        contains(assetName, term, false) ||
        contains(assetAlias, term, false) ||
        contains(externalId, term, false) ||
        isLicenseMatched
      ) {
        return true;
      }
      if (assets) {
        const associatedAssets = safeJSONParse(assets);
        return associatedAssets?.some?.(
          ({name}) => contains(name, term, false)
        );
      }
      return false;
    })
  ));
};

/**
 * The createV2SearchPayload utility function accepts filter params & returns payload object
 * which is in required format.
 *
 * ```typescript
 *
 *  import {createV2SearchPayload} from '@ion/core/src/services';
 *
 *  const payload = createV2SearchPayload(groups, geometry, assets);
 *
 * ```
 */
export const createV2SearchPayload = ({
  assets,
  assetId,
  assetStatus,
  categories,
  devices,
  geoFences,
  geometry,
  groups,
  ops,
  routes,
  searchByIds,
  searchTerm,
  searchtype,
  types,
  geometryType = 'esriGeometryPolygon'
}: QueryParams) => {
  const payload: V2SearchPayloadResult = {};
  let assetIds = [];
  if (groups?.length) {
    payload.accountid = convertStringsToNumbers(groups);
  }
  if (assets?.length) {
    assetIds = [...assetIds, ...convertStringsToNumbers(assets, 'id')];
  }
  if (assetId) {
    assetIds = [...assetIds, assetId];
  }
  if (searchByIds?.length) {
    assetIds = [...assetIds, ...searchByIds];
  }
  if (assetIds?.length) {
    payload.assetid = assetIds;
  }
  if (devices?.length) {
    payload.deviceid = convertStringsToNumbers(devices, 'id');
  }
  if (categories?.length) {
    payload.assetCategory = normalizeGlobalFilterText(categories);
  }
  if (types?.length) {
    payload.assetType = normalizeGlobalFilterText(types);
  }
  if (assetStatus?.length) {
    payload.assetStatus = normalizeGlobalFilterText(assetStatus);
  }
  if (routes?.length) {
    payload.routeId = routes;
  }
  if (geoFences?.length) {
    payload.landmark = geoFences;
  }
  if (searchTerm?.length) {
    payload.searchterm = searchTerm;
  }
  if (searchtype?.length) {
    payload.searchtype = searchtype;
  }
  if (ops?.length) {
    payload.ops = ops;
  }
  if (geometry?.length) {
    payload.geometry = {
      rings            : geometry,
      spatialReference : {
        wkid : 102100
      }
    };
    payload.geometrytype = geometryType;
  }
  return payload;
};
